import type { PanelGroupStorage } from "react-resizable-panels"
import { fetchUserPreferences, saveUserPreference } from "@/lib/user-preferences"

/**
 * Dikey ayraç genişliklerinin KULLANICI BAZLI, SUNUCU TARAFLI deposu.
 *
 * Önceden kütüphanenin varsayılanı kullanılıyordu: `localStorage`. Yani ayarlar
 * cihaza bağlıydı — kullanıcı dizüstünde kurduğu düzeni masaüstünde bulamıyor,
 * tarayıcı verisi temizlenince kaybediyor, aynı bilgisayarı paylaşan iki kişi
 * birbirinin düzenini eziyordu.
 *
 * Kütüphanenin `storage` kancası SENKRON (`getItem` anında string döndürmeli),
 * sunucu ise değil. Köprü şu: tercihler oturum açılışında TEK istekle çekilip
 * bellekte tutuluyor (`loadPanelLayouts`), `getItem` o bellekten okuyor,
 * `setItem` belleğe yazıp sunucuya gecikmeli tek bir yazma planlıyor.
 *
 * SIRA ÖNEMLİ: kütüphane kayıtlı yerleşimi yalnız BİR KEZ, panel grubu
 * bağlanırken okur. Önbellek o andan önce dolmazsa grup varsayılan ölçülerle
 * açılır ve ilk sürüklemede o varsayılanı sunucudaki gerçek kayıt üstüne
 * yazar. Bu yüzden `ProtectedRoute` içerik çizmeden önce yüklemeyi bekliyor.
 */

/** Sunucudaki tercih anahtarı; tüm panel grupları tek satırda. */
const PREFERENCE_KEY = "panel-layouts"

/** Sunucuya yazma gecikmesi. Kütüphane zaten 100 ms geciktiriyor; bu, sürükleme
 *  boyunca oluşan yazmaları tek isteğe indirir. */
const WRITE_DELAY_MS = 800

/** name → kütüphanenin serileştirdiği durum (JSON metni). */
let cache: Record<string, string> = {}
let loaded = false
let loading: Promise<void> | null = null
/** Sunucuya ulaşılamadıysa `localStorage`a düşülür — özellik bozulmaz, yalnız
 *  yine cihaza bağlı kalır. */
let degraded = false
/** En son sunucuya yazılan hâl; değişmediyse istek atılmaz. */
let lastSaved = "{}"
let timer: ReturnType<typeof setTimeout> | null = null

const local = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}

/**
 * Tercihleri çeker ve önbelleği doldurur. Aynı anda birden çok çağrı gelirse
 * tek istek yapılır; oturum boyunca da bir kez.
 */
export function loadPanelLayouts(): Promise<void> {
  if (loaded) return Promise.resolve()
  if (!loading) {
    loading = (async () => {
      try {
        const prefs = await fetchUserPreferences()
        const stored = prefs[PREFERENCE_KEY]
        const fromServer = isStringMap(stored) ? { ...stored } : {}
        /*
         * `lastSaved` SUNUCUDAKİ hâli temsil eder, önbelleğin son hâlini
         * değil: aşağıdaki taşıma önbelleği doldurduğunda aradaki fark
         * "yazılacak değişiklik" olarak görünmeli.
         */
        lastSaved = JSON.stringify(fromServer)
        cache = fromServer
        /*
         * Sunucuda kayıt yoksa bu kullanıcının bu cihazdaki mevcut düzeni
         * taşınır. Aksi hâlde özellik "herkesin ayarları sıfırlandı" diye
         * hissedilirdi; oysa amaç tam tersi.
         */
        if (Object.keys(cache).length === 0) {
          cache = readLegacyLocal()
          if (Object.keys(cache).length > 0) scheduleWrite()
        }
      } catch {
        degraded = true
      } finally {
        loaded = true
        loading = null
      }
    })()
  }
  return loading
}

/** Oturum kapanınca: sonraki kullanıcı öncekinin düzenini görmesin. */
export function resetPanelLayouts() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  cache = {}
  lastSaved = "{}"
  loaded = false
  loading = null
  degraded = false
}

export const panelStorage: PanelGroupStorage = {
  getItem(name) {
    if (degraded) return local()?.getItem(name) ?? null
    return cache[name] ?? null
  },
  setItem(name, value) {
    if (degraded) {
      local()?.setItem(name, value)
      return
    }
    if (cache[name] === value) return
    cache[name] = value
    scheduleWrite()
  },
}

function scheduleWrite() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(flush, WRITE_DELAY_MS)
}

/** Bekleyen yazmayı hemen gönderir (sekme kapanırken de çağrılıyor). */
export function flushPanelLayouts() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  void flush()
}

async function flush() {
  timer = null
  const payload = JSON.stringify(cache)
  if (payload === lastSaved) return
  try {
    await saveUserPreference(PREFERENCE_KEY, cache)
    lastSaved = payload
  } catch {
    /*
     * Yutuluyor: yerleşim kaydı kullanıcının o anki işini bölmemeli. Bir
     * sonraki sürükleme yeniden deneyecek, `lastSaved` güncellenmediği için
     * aradaki değişiklikler de kaybolmaz.
     */
  }
}

if (typeof window !== "undefined") {
  // Sekme kapanır/gizlenirken son sürükleme kaybolmasın.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPanelLayouts()
  })
}

const isStringMap = (v: unknown): v is Record<string, string> =>
  !!v && typeof v === "object" && !Array.isArray(v)
    && Object.values(v as Record<string, unknown>).every((x) => typeof x === "string")

/**
 * Bu cihazda daha önce `localStorage`a yazılmış yerleşimler.
 * Kütüphane anahtarları `react-resizable-panels:` ile başlıyor.
 */
function readLegacyLocal(): Record<string, string> {
  const store = local()
  if (!store) return {}
  const out: Record<string, string> = {}
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (!key?.startsWith("react-resizable-panels:")) continue
    const value = store.getItem(key)
    if (value) out[key] = value
  }
  return out
}
